import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import {
	Autocomplete,
	CircularProgress,
	Grid,
	InputAdornment,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Typography,
	ListSubheader,
} from '@mui/material';

import HtmlParser from '../../../components/HtmlParser';
import { useFetchInstrumentsByCompanyQuery } from '../../../services/instrumentsApi';
import type { AppDispatch } from '../../../store/store';
import { Instrument } from '../../../types/instrument';
import { selectInstrument } from './slice';
import { useIntl } from 'react-intl';
interface InstrumentSearchProps {
	onSelectInstrument?: (instrument: Instrument | null) => void;
}

const InstrumentSearch: FC<InstrumentSearchProps> = ({ onSelectInstrument }) => {
	const theme = useTheme();
	const intl = useIntl();
	const dispatch = useDispatch<AppDispatch>();
	const [value, setValue] = useState<string>('');

	const { data: instruments, isLoading: loading } = useFetchInstrumentsByCompanyQuery();

	const getOptionLabel = (option: any) => {
		if (typeof option === 'string') {
			return option;
		}
		return `${option.name} - ${option.provider}` || '';
	};

	const onSelectInstrumentHandler = async (_event: any, newValue: any) => {
		if (onSelectInstrument) {
			onSelectInstrument(newValue);
		}
		if (newValue) {
			await dispatch(selectInstrument(newValue));
		}
	};

	const onChangeName = (event: any) => setValue(event.target.value);

	const options = instruments ?? [];

	const renderInput = (params: any) => (
		<TextField
			variant='outlined'
			placeholder={intl.formatMessage({ id: 'instrumentSearch.searchPlaceholder' })}
			onChange={onChangeName}
			size='small'
			{...params}
			InputProps={{
				...params.InputProps,
				startAdornment: (
					<InputAdornment position='start'>
						<SearchIcon color='primary' fontSize='small' />
					</InputAdornment>
				),
				endAdornment: (
					<>
						{loading ? <CircularProgress color='inherit' size={16} /> : null}
						{params.InputProps?.endAdornment}
					</>
				),
			}}
		/>
	);

	const renderOption = (props: any, option: Instrument) => (
		<ListItem
			key={option.instrumentId}
			alignItems='center'
			{...props}
			sx={{
				py: 0.5,
				'&:hover': {
					backgroundColor: theme.palette.action.hover,
				},
			}}
		>
			<Grid container>
				<Grid item>
					<ListItemText
						primary={
							<Typography component='div' color='primary' variant='body2'>
								{option.name} - {option.provider}
							</Typography>
						}
						// secondary={
						// 	<Typography component='div' variant='body2' color='text.secondary'>
						// 		{option.ingress ? HtmlParser(option.ingress) : null}
						// 	</Typography>
						// }
					/>
				</Grid>
			</Grid>
		</ListItem>
	);

	return (
		<Autocomplete
			freeSolo
			fullWidth
			disableClearable
			clearOnEscape
			getOptionLabel={getOptionLabel}
			options={
				instruments?.slice().sort((a, b) => {
					const nameA = a.provider?.toUpperCase() ?? '';
					const nameB = b.provider?.toUpperCase() ?? '';
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				}) ?? []
			}
			groupBy={option => option.provider ?? 'Muut'}
			// renderGroup={params => (
			// 	<li key={params.group}>
			// 		<ListSubheader
			// 			sx={{
			// 				backgroundColor: theme.palette.background.paper,
			// 				color: theme.palette.primary.main,
			// 				fontWeight: 'bold',
			// 				py: 0.5,
			// 			}}
			// 		>
			// 			{params.group}
			// 		</ListSubheader>
			// 		{params.children}
			// 	</li>
			// )}
			value={value}
			onChange={onSelectInstrumentHandler}
			loading={loading}
			renderInput={renderInput}
			renderOption={renderOption}
			size='small'
			slotProps={{
				popper: {
					sx: {
						width: '100% !important',
						maxWidth: '60rem !important',
					},
				},
			}}
		/>
	);
};

export default InstrumentSearch;
